import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';

const ERROR_MESSAGE_KEYS = ['errorMessage', 'message'] as const;

/**
 * Reads the first non-empty string message from an error body, checking
 * `errorMessage` before `message`. String bodies are parsed as JSON first;
 * unparseable strings yield no message.
 */
const readMessageFromBody = (body: unknown): string | undefined => {
  let parsed = body;
  if (typeof body === 'string') {
    try {
      parsed = JSON.parse(body);
    } catch {
      return undefined;
    }
  }

  if (!parsed || typeof parsed !== 'object') {
    return undefined;
  }

  for (const key of ERROR_MESSAGE_KEYS) {
    const value = (parsed as Record<string, unknown>)[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return undefined;
};

/**
 * Extracts a user-facing message from a failed API call.
 *
 * For an HTTP 400 response, returns the backend's `errorMessage` (or, failing
 * that, `message`) from the body — e.g. a duplicate-name validation error.
 * A JSON string body is parsed first. Otherwise returns the provided fallback.
 *
 * @param err - The error emitted by the HTTP observable.
 * @param fallback - The message to show when no usable backend message exists.
 * @returns The backend message or the fallback.
 *
 * @example
 * error: (err) => this._toastService.showError(getApiErrorMessage(err, 'Failed to save'))
 */
export const getApiErrorMessage = (err: unknown, fallback: string): string => {
  if (
    err instanceof HttpErrorResponse &&
    err.status === HttpStatusCode.BadRequest
  ) {
    return readMessageFromBody(err.error) ?? fallback;
  }
  return fallback;
};
